import { Box, Button, Dialog, DialogContent, Paper, Slide, styled, TextField, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { useGateApi } from '../Api/useGateApi';
import { AddOrCreateUsersRequest, CreateInfo, UserCreateModelDTO, UsersGateApiClient } from '../Api/ApiServerHooks';
import { useTranslation } from 'react-i18next';
import { Upload } from '@mui/icons-material';
import GiffLoader from '../Shared/GiffLoader';
import * as XLSX from 'xlsx';
import CreateErrorsComponent from './CreateErrorsComponent';


export const ImageUploadArea = styled(Paper)(({ theme }) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
    },
}));

interface ImportUsersExcelDialogProps {
    tenantId?: number;
    isOpen: boolean;
    onClose: () => void;
}
export default function ImportUsersExcelDialog(props: Readonly<ImportUsersExcelDialogProps>) {
    const { isOpen, onClose, tenantId } = props;
    const [isDragging, setIsDragging] = useState(false);
    const [importing, setImporting] = useState(false);
    const [createErrors, setCreateErrors] = useState<CreateInfo[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { authorizedApi } = useGateApi(UsersGateApiClient);

    useEffect(() => {
        if (!isOpen) {
            setIsDragging(false);
            setImporting(false);
            setCreateErrors([]);
        }
    }, [isOpen]);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                /* authorizedApi.changeAppCustomLogo(data?.id!, {
                    data: file,
                    fileName: file.name
                });
                const reader = new FileReader();
                reader.onload = (e) => {
                    setLogoPreview(e.target?.result as string);
                };
                reader.readAsDataURL(file); */
            }
        }
    };

    const parseExcelFile = async (file: File): Promise<UserCreateModelDTO[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    const workbook = XLSX.read(data, { type: 'array' });
                    
                    // Get first sheet
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Convert to JSON with header row
                    const jsonData = XLSX.utils.sheet_to_json<any>(worksheet, { header: 1 });
                    
                    // Skip header row and parse data
                    const users: UserCreateModelDTO[] = jsonData.slice(1).map((row: any[]) => new UserCreateModelDTO({
                        firstName: row[0] || '', // Column B - Příjmení
                        lastName: row[1] || '',  // Column A - Jméno
                        email: row[2] || '',     // Column C - Email
                        msIdentity: row[3] === 'ANO', // Column D - Přihlášení MS identitou
                        language: row[4] || 'CS', // Column E - Jazyk
                        userRole: row[5] || '', // Column F - Role uživatele
                    })).filter(user => user.email);
                    
                    resolve(users);
                } catch (error) {
                    reject(error);
                }
            };
            
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('File change event:', e);
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                file.type === 'application/vnd.ms-excel' ||
                file.name.endsWith('.xlsx') || 
                file.name.endsWith('.xls')) {
                setImporting(true);
                try {
                    const users = await parseExcelFile(file);
                    console.log('Parsed users:', users);

                    let res = await authorizedApi?.addOrCreateUsers(new AddOrCreateUsersRequest({
                        tenantId: tenantId!,
                        users: users
                    }));

                    console.log('Import response:', res);
                    setCreateErrors(res?.createErrors || []);

                    setImporting(false);
                    // onClose(); // Close after successful import
                } catch (error) {
                    console.error('Error parsing Excel file:', error);
                    setImporting(false);
                    // Show error message to user
                }
            }
        }
    };

    return (
        <Dialog onClose={() => onClose()} open={isOpen}>
            <MuiDialogHeader showCloseText title={t('Administration.ImportUsersExcel')} onClose={() => onClose()} />
            <DialogContent>
                {!importing && createErrors.length === 0 && <Box>
                    <ImageUploadArea
                        sx={{
                            m: 2,
                            borderColor: isDragging ? 'primary.main' : 'grey.300',
                            backgroundColor: isDragging ? 'action.hover' : 'inherit',
                            // ...existing sx props...
                        }}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            onChange={handleFileChange}
                        />
                        <Upload sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">
                            {t('chooseFile')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {t('OrDragFile')}
                        </Typography>
                    </ImageUploadArea>
                </Box>}
                {importing && createErrors.length === 0 && <Box>
                    <GiffLoader />
                </Box>}
                {!importing && createErrors.length > 0 && <Box>
                    <CreateErrorsComponent errors={createErrors} />
                </Box>}
            </DialogContent>
        </Dialog>
    )
}
